import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { ActionIcon, Box, Button, Flex, Menu, rem, Tooltip } from '@mantine/core';
import { Row as TanstackRow } from '@tanstack/react-table';
import { useState } from 'react';
import { RegularViewTableItemData } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { HoldingEventRes, TransactionType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import type { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	PredefinedIcons,
	PredefinedIconSize,
} from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';

interface TransactionActionCellProps<T extends RecordAny> {
	row: { original: T };
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>;
	setShowEditTransactionDrawer?: (value: boolean) => void;
	setShowPairTransactionDrawer?: (value: boolean) => void;
	setShowSplitTransactionDrawer?: (value: boolean) => void;
	setShowReorderTransactionDrawer?: (transaction: T) => void;
	setEditTransaction?: (transaction: T) => void;
	setSplitTransaction?: (transaction: T) => void;
	setPairTransaction?: (transaction: T) => void;
	canShowReorder?: boolean;
}

const TransactionActionCell = <T extends RecordAny>({
	row,
	tCapManage,
	setShowEditTransactionDrawer,
	setShowPairTransactionDrawer,
	setShowSplitTransactionDrawer,
	setShowReorderTransactionDrawer,
	setEditTransaction,
	setSplitTransaction,
	setPairTransaction,
	canShowReorder,
}: TransactionActionCellProps<T>) => {
	const showReorder = canShowReorder ?? row.original.transactionError === 'OVERSOLD';
	const shouldDisable =
		row.original.type === TransactionType.ISSUE ||
		row.original.type === TransactionType.CANCELLATION ||
		row.original.type === TransactionType.REDEMPTION ||
		(row.original.sourceId === undefined && row.original.destinationId === undefined);
	const [open, setOpen] = useState(false);

	return (
		<Flex gap=".25rem" align="center" justify="flex-end" m="-.25rem 0" w="100%">
			<Menu
				width={240}
				shadow="md"
				position="bottom-end"
				opened={open}
				onClose={() => setOpen(false)}
				onOpen={() => setOpen(true)}
				withinPortal
			>
				<Menu.Target>
					<Button
						variant="transparent"
						fw={400}
						data-testid={TEST_ELEMENT_IDS.CAP_TRANSACTION_ROW_ACTIONS_BUTTON}
						onClick={(event) => event.stopPropagation()}
						rightSection={<Icon variant="chevron-blue" size="md" />}
					>
						{tCapManage('transactions.actions.tooltip')}
					</Button>
				</Menu.Target>

				<Menu.Dropdown
					sx={(theme) => ({
						boxShadow: '0px 16px 32px 0px #DCDFE0',
						borderColor: theme.colors.gray[0],
					})}
				>
					<Flex gap="0.25rem" direction="column" align="flex-start">
						{showReorder && (
							<ActionIconMenu
								label={tCapManage('transactions.reorder.tooltip')}
								testId={TEST_ELEMENT_IDS.CAP_TRANSACTION_ROW_REORDER_ACTION}
								onClick={() => {
									setShowReorderTransactionDrawer &&
										setShowReorderTransactionDrawer(row.original);
									setOpen(false);
								}}
								icon="reorder"
							/>
						)}

						<ActionIconMenu
							label={tCapManage('transactions.split.tooltip')}
							onClick={() => {
								setShowSplitTransactionDrawer &&
									setShowSplitTransactionDrawer(true);
								setSplitTransaction && setSplitTransaction(row.original);
							}}
							icon="split"
						/>

						<ActionIconMenu
							label={tCapManage('transactions.pair.tooltip')}
							onClick={() => {
								setShowPairTransactionDrawer && setShowPairTransactionDrawer(true);
								setPairTransaction && setPairTransaction(row.original);
							}}
							icon="pair"
							disabled={shouldDisable}
						/>

						<ActionIconMenu
							label={tCapManage('transactions.preview.tooltip')}
							onClick={() => {
								setShowEditTransactionDrawer && setShowEditTransactionDrawer(true);
								setEditTransaction && setEditTransaction(row.original);
							}}
							icon="eye"
						/>
					</Flex>
				</Menu.Dropdown>
			</Menu>
		</Flex>
	);
};

export const createSecurityActionColumn = <T extends RecordAny>(
	setShowEditSecurityDrawer?: (value: boolean) => void,
	setEditSecurity?: (transaction: T) => void
): ExtendedColumnDef<T> => {
	return {
		cell: ({ row }) => (
			<Flex justify="flex-end" w="100%">
				<ActionIconWithTooltip
					label="Open"
					testId={TEST_ELEMENT_IDS.CAP_SECURITY_ROW_OPEN_BUTTON}
					onClick={() => {
						setShowEditSecurityDrawer && setShowEditSecurityDrawer(true);
						setEditSecurity && setEditSecurity(row.original);
					}}
					icon="eye"
				/>
			</Flex>
		),
		header: '',
		id: 'actions',
		enableGlobalFilter: false,
		enableColumnFilter: false,
		filter: () => <div />,
		size: 80,
		meta: {
			isGrow: true,
		},
	};
};

export const createSecurityEventActionColumn = <T extends RecordAny>(
	setShowEditSecurityEventDrawer?: (value: boolean) => void,
	setSecurityEventEdit?: (transaction: T) => void
): ExtendedColumnDef<T> => {
	return {
		cell: ({ row }) => (
			<Flex justify="flex-end" w="100%">
				<ActionIconWithTooltip
					label="Open"
					onClick={() => {
						setShowEditSecurityEventDrawer?.(true);
						setSecurityEventEdit?.(row.original);
					}}
					icon="eye"
				/>
			</Flex>
		),
		header: '',
		id: 'actions',
		enableGlobalFilter: false,
		enableColumnFilter: false,
		filter: () => <div />,
		size: 80,
	};
};

const ActionIconWithTooltip = ({
	label,
	icon,
	onClick,
	disabled = false,
	testId,
}: {
	label: string;
	icon: PredefinedIcons;
	onClick?: () => void;
	disabled?: boolean;
	testId?: string;
}) => {
	return (
		<Tooltip
			label={label}
			position="top-end"
			sx={(theme) => ({
				backgroundColor: disabled ? theme.colors.gray[5] : theme.colors.gray[2],
				color: disabled ? theme.colors.dark[3] : theme.colors.dark[4],
			})}
		>
			<ActionIcon
				data-testid={testId}
				onClick={(event) => {
					if (onClick) {
						event.stopPropagation();
						onClick();
					}
				}}
				sx={(theme) => ({
					color: disabled ? theme.colors.gray[5] : theme.colors.dark[0],
					padding: '0.5rem',
					height: rem('32px'),
					width: 'unset',
					border: 'none',
					cursor: disabled ? 'not-allowed' : 'pointer',
					'&:hover': {
						color: !disabled ? theme.colors.blue[6] : theme.colors.gray[5],
						backgroundColor: 'transparent',
					},
					opacity: disabled ? 0.5 : 1,
				})}
				disabled={disabled}
			>
				<Icon variant={icon} size="xl" />
			</ActionIcon>
		</Tooltip>
	);
};

export const ActionIconMenu = ({
	label,
	icon,
	onClick,
	disabled = false,
	size = 'lg',
	testId,
}: {
	label: string;
	icon: PredefinedIcons;
	onClick?: () => void;
	disabled?: boolean;
	size?: PredefinedIconSize;
	testId?: string;
}) => {
	return (
		<Box
			data-testid={testId}
			onClick={(event) => {
				if (onClick && !disabled) {
					event.stopPropagation();
					onClick();
				}
			}}
			sx={(theme) => ({
				color: theme.colors.dark[0],
				padding: '0.5rem',
				height: 'unset',
				border: 'none',
				cursor: disabled ? 'not-allowed' : 'pointer',
				opacity: disabled ? 0.6 : 1,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'flex-start',
				gap: '8px',
				width: '100%',
				borderRadius: theme.radius.sm,
				'&:hover': !disabled
					? {
							backgroundColor: theme.colors.gray[0],
						}
					: {},
			})}
		>
			<Icon variant={icon} size={size} />
			{label}
		</Box>
	);
};

const RestoreIconMenu = ({
	label,
	icon,
	onClick,
	disabled = false,
}: {
	label: string;
	icon: PredefinedIcons;
	onClick?: () => void;
	disabled?: boolean;
}) => {
	return (
		<ActionIcon
			onClick={(event) => {
				if (onClick) {
					event.stopPropagation();
					onClick();
				}
			}}
			sx={(theme) => ({
				color: theme.colors.blue[6],
				padding: '0.5rem',
				height: 'unset',
				border: 'none',
				cursor: 'pointer',
				opacity: 1,
				display: 'flex',
				justifyContent: 'flex-start',
				gap: '8px',
				width: '100%',
			})}
			disabled={disabled}
		>
			<Icon variant={icon} size="lg" />
			{label}
		</ActionIcon>
	);
};

export const createTransactionActionColumn = <T extends RecordAny>(
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	setShowEditTransactionDrawer?: (value: boolean) => void,
	setShowPairTransactionDrawer?: (value: boolean) => void,
	setShowSplitTransactionDrawer?: (value: boolean) => void,
	setShowReorderTransactionDrawer?: (transaction: T) => void,
	setEditTransaction?: (transaction: T) => void,
	setSplitTransaction?: (transaction: T) => void,
	setPairTransaction?: (transaction: T) => void,
	canShowReorder?: (row: T) => boolean
): ExtendedColumnDef<T> => {
	return {
		cell: ({ row }) => (
			<TransactionActionCell
				row={row}
				tCapManage={tCapManage}
				setShowEditTransactionDrawer={setShowEditTransactionDrawer}
				setShowPairTransactionDrawer={setShowPairTransactionDrawer}
				setShowSplitTransactionDrawer={setShowSplitTransactionDrawer}
				setShowReorderTransactionDrawer={setShowReorderTransactionDrawer}
				setEditTransaction={setEditTransaction}
				setSplitTransaction={setSplitTransaction}
				setPairTransaction={setPairTransaction}
				canShowReorder={canShowReorder?.(row.original)}
			/>
		),
		header: '',
		id: 'actions',
		enableGlobalFilter: false,
		enableColumnFilter: false,
		size: 120,
		meta: {
			isGrow: false,
		},
		filter: () => <div />,
	};
};

export const createTransactionRestoreColumn = <T extends RecordAny>(
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	restoreTransaction?: (transactionId: string) => void
): ExtendedColumnDef<T> => {
	return {
		cell: ({ row }) => {
			return (
				<RestoreIconMenu
					label={tCapManage('transactions.restore.tooltip')}
					onClick={() => {
						restoreTransaction && restoreTransaction(row.original.id);
					}}
					icon="restore"
				/>
			);
		},
		header: 'Restore',
		id: 'restore',
		enableGlobalFilter: false,
		enableColumnFilter: false,
		filter: () => <div />,
		size: 80,
	};
};

interface HoldingActionCellProps {
	row: TanstackRow<RegularViewTableItemData>;
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>;
	onCreateEvent?: (holding: RegularViewTableItemData) => void;
	onEditEvent?: (holding: RegularViewTableItemData, events: HoldingEventRes[]) => void;
	onDeleteEvent?: (holding: RegularViewTableItemData, events: HoldingEventRes[]) => void;
}

const HoldingActionCell = ({
	row,
	tCapManage,
	onCreateEvent,
	onEditEvent,
	onDeleteEvent,
}: HoldingActionCellProps) => {
	const [open, setOpen] = useState(false);

	const events = row.original.events || [];
	const eventTypes = new Set(events.map((event: HoldingEventRes) => event.type));
	const hasAllEventTypes = eventTypes.size >= 4;
	const hasEvents = events.length > 0;

	const handleEditEventClick = () => {
		onEditEvent?.(row.original, events);
	};

	const handleCreateEventClick = () => {
		onCreateEvent?.(row.original);
	};

	const handleDeleteEventClick = () => {
		onDeleteEvent?.(row.original, events);
	};

	return (
		<Flex gap=".25rem" align="center" justify="flex-end" m="-.25rem 0" w="100%">
			<Menu
				width={240}
				shadow="md"
				position="bottom-end"
				opened={open}
				onClose={() => setOpen(false)}
				onOpen={() => setOpen(true)}
				withinPortal
			>
				<Menu.Target>
					<Button
						variant="subtle"
						color="gray"
						onClick={(event) => {
							event.stopPropagation();
						}}
					>
						<Icon variant="holding-group" size="lg" />
					</Button>
				</Menu.Target>

				<Menu.Dropdown
					sx={(theme) => ({
						boxShadow: '0px 16px 32px 0px #DCDFE0',
						borderColor: theme.colors.gray[0],
					})}
				>
					<Flex gap="0.25rem" direction="column" align="flex-start">
						<ActionIconMenu
							label={tCapManage('holdings.event.add')}
							onClick={handleCreateEventClick}
							icon="holdings-add"
							size="lg"
							disabled={hasAllEventTypes}
						/>
						<ActionIconMenu
							label={tCapManage('holdings.event.edit')}
							onClick={handleEditEventClick}
							icon="pencil"
							size="lg"
							disabled={!hasEvents}
						/>
						<ActionIconMenu
							label={tCapManage('holdings.event.remove')}
							onClick={handleDeleteEventClick}
							icon="holdings-remove"
							size="lg"
							disabled={!hasEvents}
						/>
					</Flex>
				</Menu.Dropdown>
			</Menu>
		</Flex>
	);
};

export const createHoldingActionColumn = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	onCreateEvent?: (holding: RegularViewTableItemData) => void,
	onEditEvent?: (holding: RegularViewTableItemData, events: HoldingEventRes[]) => void,
	onDeleteEvent?: (holding: RegularViewTableItemData, events: HoldingEventRes[]) => void
) => {
	return {
		cell: ({ row }) => {
			return (
				<HoldingActionCell
					row={row}
					tCapManage={tCapManage}
					onCreateEvent={onCreateEvent}
					onEditEvent={onEditEvent}
					onDeleteEvent={onDeleteEvent}
				/>
			);
		},
		header: '',
		id: 'actions',
		enableGlobalFilter: false,
		enableColumnActions: false,
		enableColumnFilter: false,
		filter: () => <div />,
		size: 80,
	};
};
